import {
  IPlanOverviewDetailReq,
  IUserLoginReq,
} from '@models/interfaces/user.interface';
import userService from '@services/GeniebookAPI/UserServices.services';
import Cookies from 'js-cookie';
import { useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { SLOW_DATA_STALE_TIME_IN_SECONDS } from '@utils/constants/global.constants';
import { useAtomValue } from 'jotai';
import { subjectSubscriptionsQueryAtom } from '@atom/globalQueryAtom';
import { JC_SUBSCRIPTION_PLAN } from '@components/subscription/helper/SubscriptionConstant';
import { JUNIOR_COLLEGE_STUDENT } from '@utils/constants/user.constants';

export const useLoginMutation = () => {
  return useMutation((data: IUserLoginReq) => userService.login(data), {
    onSuccess: data => {
      if (data.data.error) {
        const domain = window.location.hostname.replace(
          /(.*\.)?([^.]*\..*)/,
          '$2',
        );
        Cookies.set('uid', '', {
          expires: new Date(0),
          path: '/',
          domain: domain,
        });
        Cookies.set('gbadmin_token', '', {
          expires: new Date(0),
          path: '/',
          domain: domain,
        });
        Cookies.set('gb_bubbly_cookie', '', {
          expires: new Date(0),
          path: '/',
          domain: domain,
        });
      }
    },
  });
};

export const useVerifyLogin = () => {
  const navigate = useNavigate();

  return useMutation((uid: string) => userService.verifyLogin(uid), {
    onSuccess: data => {
      if (data.data.error) {
        const domain = window.location.hostname.replace(
          /(.*\.)?([^.]*\..*)/,
          '$2',
        );
        Cookies.set('uid', '', {
          expires: new Date(0),
          path: '/',
          domain: domain,
        });
        Cookies.set('gbadmin_token', '', {
          expires: new Date(0),
          path: '/',
          domain: domain,
        });
        Cookies.set('gb_bubbly_cookie', '', {
          expires: new Date(0),
          path: '/',
          domain: domain,
        });
        navigate('/');
      }
    },
  });
};

export const useUserDetails = () => {
  return useQuery(['userDetails'], userService.userDetail, {
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
  });
};

export const useSchoolList = () => {
  return useQuery(['schoolList'], userService.getSchoolList);
};

export const useSchoolDetails = (school_id: number) => {
  return useQuery({
    queryKey: ['schoolDetails', school_id],
    queryFn: () => userService.getSchoolDetails(school_id),
  });
};

export function useNotifications(limit: number, pageParam?: number) {
  return useInfiniteQuery({
    queryKey: ['notification', limit, pageParam],
    queryFn: async ({ pageParam }) => {
      const rawData = await userService.getNotifications(limit, pageParam);
      return rawData.data?.data;
    },
    getNextPageParam: lastPage => {
      if (!Array.isArray(lastPage)) {
        return;
      }
      if (lastPage.length === 0) {
        return undefined;
      }
      return lastPage[lastPage.length - 1].notification_id;
    },
  });
}

export function useUnreadNotificationCount() {
  return useQuery({
    queryKey: ['unreadNotificationCount'],
    queryFn: async () => {
      const rawData = await userService.userUnreadNotification();
      return rawData.data;
    },
    cacheTime: 0,
  });
}

export function useSubscriptionData() {
  return useQuery({
    queryKey: ['subscriptionData'],
    queryFn: userService.getSubjectSubscription,
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
  });
}

export function useExamTypeList() {
  return useQuery({
    queryKey: ['examType'],
    queryFn: userService.getExamTypes,
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
  });
}

export function useVerifyBubbleStoreLogin() {
  return useMutation({
    mutationFn: () => userService.goToBubbleStore(),
    onSuccess: data => {
      if (
        !data?.data ||
        data.data.bubbly_url == undefined ||
        data.data.bubbly_uid == undefined
      )
        return;
      const link = data.data.bubbly_url + '?bubbly_uid=' + data.data.bubbly_uid;
      window.open(link, '_blank');
    },
  });
}

export const useUpdateNPSStatus = () => {
  return useMutation((action_type: number) =>
    userService.updateNPSStatus(action_type),
  );
};

export const useUpdateMasterySurveyStatus = () => {
  return useMutation(() => userService.updateMasterySurveyStatus());
};

export const useGetStudentOnboardingMasteryLevelPermission = () => {
  return useMutation(() =>
    userService.getStudentOnboardingMasteryLevelPermission(),
  );
};

export const useStudentOnboardingTooltipPermission = () => {
  return useQuery(
    ['studentOnboardingTooltipPermission'],
    userService.getStudentOnboardingTooltipPermission,
    {
      staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
    },
  );
};

export function useTopUpGenieCreditList() {
  return useQuery({
    queryKey: ['genie-credit-list'],
    queryFn: () => userService.getTopUpGenieCreditList(),
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
  });
}

export function usePlanOverviewDetail(formData: IPlanOverviewDetailReq) {
  return useQuery({
    queryKey: ['plan-overview-detail', formData],
    queryFn: () => userService.getPlanOverviewDetail(formData),
    staleTime: SLOW_DATA_STALE_TIME_IN_SECONDS * 1000,
    enabled: !!formData.subject_id,
  });
}

export function usePlanRecommendation(enable: boolean, isChinese: boolean) {
  return useQuery({
    queryKey: ['plan-overview-detail'],
    queryFn: () =>
      userService.getPlanOverviewDetail({ is_chinese: isChinese ? 1 : 0 }),
    staleTime: 60 * 1000,
    enabled: enable,
  });
}

export const useIsJCUser = ({
  subjectId,
  generalPage = false,
}: {
  subjectId: number | undefined;
  generalPage?: boolean;
}) => {
  const userDetailsQuery = useUserDetails();
  const userDetails = userDetailsQuery.data?.data;
  const subscriptionsQuery = useAtomValue(subjectSubscriptionsQueryAtom);
  const subjectSubscriptionPlan = subscriptionsQuery.data?.data.data?.find(
    item => Number(item.subject_id) === subjectId,
  );

  const isJC = () => {
    const JCLevel = Object.values(JUNIOR_COLLEGE_STUDENT) as string[];
    const requiredSubs = Object.values(JC_SUBSCRIPTION_PLAN) as string[];

    if (!subjectId && generalPage === false) {
      return undefined;
    }

    if (userDetails && subscriptionsQuery.data?.data) {
      const levelIsJC = JCLevel.includes(userDetails.system_level);
      if (subjectId) {
        const subIsHybridOnline = requiredSubs.includes(
          String(subjectSubscriptionPlan?.subscription_plan_id),
        );
        return levelIsJC && subIsHybridOnline;
      } else if (generalPage) {
        // for general page, Online Hybrid subscription will ignore the subject
        const subscribedToOnlineHybrid =
          subscriptionsQuery.data?.data?.data.find(item =>
            requiredSubs.includes(String(item.subscription_plan_id)),
          );
        return levelIsJC && Boolean(subscribedToOnlineHybrid);
      }
    }
  };

  return {
    isJC: isJC(),
    subscriptionPlanId: subjectSubscriptionPlan,
  };
};
